import { useEffect, useState } from "react";
import { Plus, Trash2, Camera, ArrowUp, ArrowDown, Save, Link2, Send, Loader2, FileSpreadsheet, Copy, CalendarDays, X, UserPlus, UserMinus, Upload as Upload2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DAYS, FREQS, NTHS, NEEDS_DAYS, cadence, nextDates, occursOn, ordinal, pretty } from "./util";
import { WhoPill, WhoDialog } from "./WhoDoing";

const input = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-semibold">{label}{hint && <span className="font-normal text-muted-foreground"> — {hint}</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);

// A task scoped to specific weekdays must SHOW those weekdays — reading one back as its first
// day silently collapsed an imported "Mon/Wed/Fri" row to Monday.
const taskDays = (t) => (Array.isArray(t.days) ? t.days : []);
const dayList = (t) => taskDays(t).map((d) => (DAYS.find((x) => x[0] === d) || [])[1]).filter(Boolean).join(", ");

// Checkpoints inside one task: "Clean the amenity room" → corners, windows, chairs. The task ticks
// itself when they are all ticked, so this is detail, never another line on the list.
const Steps = ({ task, i, onChange }) => {
  const [text, setText] = useState("");
  const steps = task.steps || [];
  const add = () => {
    const label = text.trim();
    if (!label) return;
    onChange([...steps, { label }]);
    setText("");
  };
  return (
    <div className="mt-2 w-full border-t border-sand-200 pt-2 sm:basis-full" data-testid={`editor-steps-${i}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {steps.map((s, j) => (
          <span key={j} data-testid={`editor-step-${i}-${j}`}
            className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-ink-700 ring-1 ring-sand-200">
            {s.label}
            <button onClick={() => onChange(steps.filter((_, k) => k !== j))} aria-label="Remove checkpoint"
              data-testid={`editor-step-remove-${i}-${j}`} className="text-sand-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <input value={text} onChange={(e) => setText(e.target.value)} data-testid={`editor-step-input-${i}`}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={steps.length ? "another checkpoint" : "+ checkpoint (corners, windows…)"}
          className="min-w-[150px] flex-1 rounded-lg bg-transparent px-2 py-1 text-xs outline-none placeholder:text-sand-400" />
        {text.trim() && (
          <button onClick={add} data-testid={`editor-step-add-${i}`}
            className="rounded-full bg-ink px-2.5 py-1 text-xs font-semibold text-white">Add</button>
        )}
      </div>
    </div>
  );
};

const blank = (cfg, vendorId) => ({
  name: "", vendor_id: vendorId || "", freq: "weekly", interval: 2, dow: [],
  month_day: 1, nth: 1, nth_dow: "mon", start_date: "", end_date: "", skip_dates: [],
  window_start: "", due_by: cfg?.due_by || "17:00",
  remind: cfg?.remind || "window_start", remind_hours: cfg?.remind_hours ?? 2,
  notify_to: cfg?.notify_to || "both", auto_approve: !!cfg?.auto_approve, crew_can_add: null,
  nudge_minutes: "", mark_missed: "", tasks: [],
});

// A checklist saved before any of the recurrence options existed carries only `dow`, so every
// missing key is filled from the blank — the controls must never flip to uncontrolled.
const forEdit = (t, cfg) => ({
  ...blank(cfg, t.vendor_id), ...t,
  freq: t.freq || "weekly",
  nudge_minutes: t.nudge_minutes == null ? "" : String(t.nudge_minutes),
  mark_missed: t.mark_missed == null ? "" : (t.mark_missed ? "yes" : "no"),
  skip_dates: t.skip_dates || [],
  start_date: String(t.start_date || "").slice(0, 10),
  end_date: String(t.end_date || "").slice(0, 10),
});

export function Builder({ tenantId, templates, vendors, config, onSaved, onVendorAdded, readOnly, today }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [skip, setSkip] = useState("");
  const [asking, setAsking] = useState(null);   // a chosen file waiting on new-vs-add
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState(null);       // what the last sheet turned into
  const [monthly, setMonthly] = useState(null); // monthly rows from that sheet, waiting their turn
  const [hols, setHols] = useState([]);         // the holidays worth skipping, dated for this community
  const [showHols, setShowHols] = useState(false);
  const [invite, setInvite] = useState(null);   // adding a company that is not on the platform yet
  const [links, setLinks] = useState(null);
  const [offices, setOffices] = useState([]);   // companies with nobody in the office on file
  const [officeFor, setOfficeFor] = useState(null);
  const [whoFor, setWhoFor] = useState(null);   // the "Who's doing this?" panel, one row at a time

  const loadLinks = () => api.get(`/tenants/${tenantId}/checklists/links`)
    .then((r) => { setLinks(r.data.links || []); setOffices(r.data.offices_missing || []); })
    .catch(() => {});

  useEffect(() => {
    // A work token is a key to the vendor's screen — board seats never see it.
    if (readOnly) { setLinks([]); return; }
    loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, readOnly, vendors.length, templates]);

  // The handoff, from the office's side: the manager never needs to know the vendor's org chart.
  const markOff = async (l) => {
    if (!window.confirm(`${l.name} is no longer there?\n\nTheir link closes at the end of today, nothing else is sent to them, and ${l.vendor}'s office is asked to pick someone for anything they held.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/checklists/links/${l.contact_id}/off`, {});
      toast.success((data.lists || []).length
        ? `${l.name} is off ${data.lists.join(", ")} — the office has been asked`
        : `${l.name} is off the list`);
      await loadLinks();
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); } finally { setBusy(false); }
  };

  const saveOffice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/checklists/office`, officeFor);
      toast.success(`${data.name} is ${officeFor.vendor}'s office contact — link sent to ${data.sent_to}`);
      setOfficeFor(null);
      await loadLinks();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add them"); } finally { setBusy(false); }
  };

  useEffect(() => {
    api.get(`/tenants/${tenantId}/checklists/holidays`).then((r) => setHols(r.data.holidays || [])).catch(() => {});
  }, [tenantId]);

  const setTask = (i, patch) => setEdit((e) => ({ ...e, tasks: e.tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  const move = (i, dir) => setEdit((e) => {
    const tasks = [...e.tasks];
    const j = i + dir;
    if (j < 0 || j >= tasks.length) return e;
    [tasks[i], tasks[j]] = [tasks[j], tasks[i]];
    return { ...e, tasks };
  });

  const save = async () => {
    if (!edit.name.trim()) return toast.error("Give it a name");
    if (NEEDS_DAYS.includes(edit.freq || "weekly") && !edit.dow.length) return toast.error("Pick at least one day");
    if (!edit.tasks.length) return toast.error("Add at least one task");
    const payload = {
      ...edit,
      nudge_minutes: edit.nudge_minutes === "" ? null : Number(edit.nudge_minutes),
      mark_missed: edit.mark_missed === "yes" ? true : edit.mark_missed === "no" ? false : null,
    };
    setBusy(true);
    try {
      const { data } = edit.id
        ? await api.put(`/tenants/${tenantId}/checklists/${edit.id}`, payload)
        : await api.post(`/tenants/${tenantId}/checklists`, payload);
      const next = (data?.checklist?.next_dates || [])[0];
      // The same sheet carried monthly work. Hand it straight over rather than making the manager
      // remember it and upload the file a second time.
      if (monthly && !edit.id) {
        setEdit({ ...blank(config, edit.vendor_id), name: monthly.name,
                  freq: "monthly_day", month_day: 1, tasks: monthly.tasks });
        setMonthly(null); setRead(null);
        onSaved?.();
        toast.success(`Saved. Now the ${monthly.tasks.length} monthly tasks from the same sheet — `
                      + "pick the day of the month and save them too.");
        return;
      }
      toast.success(data?.checklist?.draft
        ? "Saved as a draft — press “Put someone on it” when you know who's doing it"
        : next ? `Saved — next visit ${pretty(next, { weekday: "short", month: "short" })}`
               : "Saved — nothing scheduled in the next six months");
      setEdit(null); setRead(null);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Turn this checklist off? Past visits stay on the record.")) return;
    try { await api.delete(`/tenants/${tenantId}/checklists/${id}`); toast.success("Turned off"); onSaved?.(); }
    catch { toast.error("Couldn't turn it off"); }
  };

  const doPaste = () => {
    // Rows pasted out of Excel or Sheets arrive tab-separated; typed rows arrive comma-separated.
    const rows = paste.split("\n").map((r) => r.trim()).filter(Boolean).map((r) => {
      const [label, section, flag] = (r.includes("\t") ? r.split("\t") : r.split(",")).map((x) => (x || "").trim());
      return { label, section: section || "Tasks", days: "all",
               photo_required: (flag || "").toLowerCase().includes("photo") || !!config?.photo_required_default };
    }).filter((r) => r.label);
    if (!rows.length) return toast.error("Nothing to read in there");
    setEdit((e) => ({ ...e, tasks: [...e.tasks, ...rows] }));
    setPaste(""); setShowPaste(false);
    toast.success(`${rows.length} tasks added`);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";                       // so the same file can be chosen twice
    if (!f) return;
    if (edit && edit.tasks.length) setAsking(f);   // new checklist, or add to the one being edited?
    else readFile(f, "new");
  };

  const readFile = async (f, mode) => {
    setAsking(null); setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/tenants/${tenantId}/checklists/import`, fd);
      const dow = data.schedule?.dow || [];
      if (mode === "add") setEdit((e) => ({ ...e, tasks: [...(e?.tasks || []), ...data.tasks] }));
      else setEdit((e) => ({ ...blank(config, e?.vendor_id || ""), name: data.name || "", tasks: data.tasks,
                             ...(dow.length ? { freq: "weekly", dow } : {}) }));
      setRead({ ...data, file: f.name, mode });
      // Monthly rows can't run on a weekly checklist. They're held, and offered as their own
      // checklist the moment this one is saved — the sheet is one job, not two uploads.
      setMonthly(data.monthly?.length
        ? { name: `${(data.name || "Checklist").slice(0, 100)} — monthly`, tasks: data.monthly } : null);
      toast.success(`Read ${data.tasks.length} tasks from ${f.name}`
        + (dow.length ? ` — ${dow.map((d) => (DAYS.find((x) => x[0] === d) || [])[1]).join(", ")}` : ""));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that sheet");
    } finally { setReading(false); }
  };

  const addVendor = async () => {
    const who = (invite.name || "").trim();
    const company = (invite.company || "").trim();
    const phone = (invite.phone || "").trim();
    const email = (invite.email || "").trim();
    if (!who) return toast.error("Who is it? A name is all I need.");
    if (!phone && !email) return toast.error("A mobile or an email — that's how they get the list");
    setBusy(true);
    try {
      // No company typed ⇒ it's a person, and a person is never sent a company's paperwork.
      const { data } = await api.post(`/tenants/${tenantId}/checklists/recipients`,
                                      { name: who, phone, email, company });
      setEdit((e) => ({ ...e, vendor_id: data.vendor_id }));
      setInvite(null);
      await onVendorAdded?.();
      if (!company) toast.success(`${who} added — their work link went to ${data.sent_to}`);
      else toast.success(data.held
        ? `${company} added — their setup link goes out at ${data.held.send_at_local}`
        : `${company} added — setup link sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add them");
    } finally { setBusy(false); }
  };

  // Plain render helpers, not components — a component redefined each render remounts the file
  // input and the native picker loses its onChange.
  const uploadBtn = (testid, label) => (
    <label data-testid={testid}
      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-sand-300 bg-white px-4 py-2.5 text-sm font-semibold hover:border-sand-400">
      {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload2 className="h-4 w-4" />}
      {reading ? "Reading the sheet…" : label}
      <input type="file" accept=".xlsx,.xlsm,.csv,.tsv" className="hidden" onChange={pickFile} disabled={reading}
        data-testid={`${testid}-input`} />
    </label>
  );

  // A draft's one missing piece is WHO. Taking the manager straight to that control beats
  // "press Edit, then find the picker" — which is what made saving a draft feel like a dead end.
  const focusVendor = () => {
    const el = document.querySelector("[data-testid='editor-vendor']");
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    el?.focus();
  };

  const holName = (iso) => (hols.find((h) => h.date === iso) || {}).name || "";
  // Would there be a visit that day IF it weren't skipped? Judged with skip_dates removed, or
  // every date already on the skip list reads back as "no visit that day", which is circular.
  const landsOn = (iso) => !!edit && occursOn({ ...edit, skip_dates: [] }, new Date(`${iso}T00:00:00Z`));

  // "I don't want to have to think of every one." The dates come from the server already placed on
  // this community's calendar, and only the ones that actually land on a visit are added — skipping
  // a Wednesday on a Mon/Thu checklist is noise, not caution.
  const addHolidays = () => {
    const evaluable = !NEEDS_DAYS.includes(edit.freq || "weekly") || (edit.dow || []).length > 0;
    const common = hols.filter((h) => h.common);
    const want = common.filter((h) => !evaluable || landsOn(h.date));
    if (!want.length) return toast.error("None of the usual holidays land on a day this one runs");
    const before = (edit.skip_dates || []).length;
    const next = [...new Set([...(edit.skip_dates || []), ...want.map((h) => h.date)])].sort();
    setEdit({ ...edit, skip_dates: next });
    const added = next.length - before;
    const off = common.length - want.length;
    toast.success(`${added || "No new"} holiday${added === 1 ? "" : "s"} skipped`
      + (off ? ` — ${off} don't land on a day this one runs` : ""));
  };

  // What "no day choice" means, said plainly. A monthly checklist has ONE visit a month, so
  // offering "Every visit" on each task read like nonsense — this says when it actually runs.
  const runsLabel = () => {
    const f = edit.freq || "weekly";
    if (f === "monthly_day") return `Once a month · the ${ordinal(Number(edit.month_day) || 1)}`;
    if (f === "monthly_nth") return "Once a month";
    if (f === "every_n_days") return Number(edit.interval) === 1 ? "Every day" : `Every ${edit.interval || 1} days`;
    return (edit.dow || []).length ? "Every visit" : "Pick the days above";
  };

  // Day scoping per task. It used to be a one-of-seven dropdown ("Mon only"), so a manager could
  // not build by hand what a spreadsheet import produced — and touching it collapsed an imported
  // "Mon, Thu" down to one day. Chips, multi-select, scoped to the days the checklist actually runs.
  const dayPick = (task, i) => {
    const scoped = taskDays(task);
    const runs = NEEDS_DAYS.includes(edit.freq || "weekly") ? (edit.dow || []) : [];
    const show = DAYS.map(([k]) => k).filter((k) => runs.includes(k) || scoped.includes(k));
    if (!show.length) {
      return (
        <span data-testid={`editor-task-days-${i}`}
          title={NEEDS_DAYS.includes(edit.freq || "weekly")
            ? "Pick which days this checklist runs and you can scope tasks to some of them"
            : "This checklist runs on one date at a time, so every task on it runs then"}
          className="block rounded-lg border border-dashed border-sand-300 px-2.5 py-2 text-xs text-muted-foreground sm:whitespace-nowrap">
          {runsLabel()}
        </span>
      );
    }
    const chip = (on, stale) => `h-9 w-full rounded-lg border px-2 text-xs font-semibold transition-colors sm:w-auto sm:min-w-[38px] ${
      on ? (stale ? "border-amber-300 bg-amber-50 text-amber-700" : "border-ink bg-ink text-white")
         : "border-sand-200 bg-white text-ink-700 hover:border-sand-400"}`;
    const toggle = (d) => {
      const next = scoped.includes(d) ? scoped.filter((x) => x !== d) : [...scoped, d];
      const keep = DAYS.map(([k]) => k).filter((k) => next.includes(k));
      // Every day it runs = every visit, which is what "all" means — don't store the long way round.
      setTask(i, { days: keep.length && !(runs.length && keep.length === runs.length && runs.every((r) => keep.includes(r))) ? keep : "all" });
    };
    return (
      <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center" data-testid={`editor-task-days-${i}`}
        title={scoped.length ? `Only on ${dayList(task)}` : "On every visit this checklist runs"}>
        <button onClick={() => setTask(i, { days: "all" })} data-testid={`editor-task-days-${i}-all`}
          className={chip(!scoped.length)}>All</button>
        {show.map((d) => (
          <button key={d} onClick={() => toggle(d)} data-testid={`editor-task-days-${i}-${d}`}
            title={runs.includes(d) ? "" : "The checklist doesn't run on this day — tap to take it off"}
            className={chip(scoped.includes(d), !runs.includes(d))}>
            {(DAYS.find((x) => x[0] === d) || [])[1]}
          </button>
        ))}
      </div>
    );
  };

  const askPanel = () => !asking ? null : (
    <div className="rounded-2xl border border-redline-200 bg-redline-50/60 p-5" data-testid="import-ask">
      <p className="text-sm font-semibold">{asking.name}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        You're part-way through “{edit?.name || "a checklist"}”. What should I do with this sheet?
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => readFile(asking, "new")} data-testid="import-ask-new"
          className="rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/90">
          Start a new checklist from it
        </button>
        <button onClick={() => readFile(asking, "add")} data-testid="import-ask-add"
          className="rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-semibold hover:border-sand-400">
          Add its rows to this one
        </button>
        <button onClick={() => setAsking(null)} data-testid="import-ask-cancel"
          className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
      </div>
    </div>
  );

  if (!edit) return (
    <div className="space-y-6" data-testid="checklists-builder">
      {whoFor && (
        <WhoDialog tenantId={tenantId} row={whoFor} onClose={() => setWhoFor(null)}
          onSaved={() => { onSaved?.(); loadLinks(); }} />
      )}
      {askPanel()}
      <div className="space-y-2">
        {templates.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-sand-200 bg-white px-5 py-4"
            data-testid={`checklist-row-${t.id}`}>
            <div className="min-w-[180px] flex-1">
              <div className="font-heading text-base font-bold tracking-tight">{t.name}</div>
              <div className="text-sm text-muted-foreground">
                {(vendors.find((v) => v.id === t.vendor_id) || {}).name || "Nobody on it yet"} · {cadence(t)} · by {t.due_by} · {t.tasks.length} tasks
                {t.auto_approve && " · approves itself when complete"}
              </div>
              {t.draft && (
                <div className="text-xs font-semibold text-amber-700" data-testid={`checklist-draft-${t.id}`}>
                  Draft — nothing is scheduled until someone's on it
                  {!readOnly && (
                    <button onClick={() => { setRead(null); setEdit(forEdit(t, config)); setTimeout(focusVendor, 120); }}
                      data-testid={`checklist-assign-${t.id}`}
                      className="ml-2 font-bold text-redline-600 underline underline-offset-2 hover:text-redline-700">
                      Put someone on it →
                    </button>
                  )}
                </div>
              )}
              {!t.draft && (t.next_dates || []).length > 0 && (
                <div className="text-xs text-muted-foreground" data-testid={`checklist-next-${t.id}`}>
                  Next: {t.next_dates.map((d) => pretty(d, { weekday: "short", month: "short" })).join(" · ")}
                </div>
              )}
              {!t.draft && !readOnly && (
                <WhoPill row={t} onClick={() => setWhoFor(t)} />
              )}
              {!t.draft && readOnly && t.holder && (
                <div className="text-xs text-muted-foreground">With {t.holder}</div>
              )}
              {!t.draft && !(t.next_dates || []).length && (
                <div className="text-xs font-semibold text-amber-700" data-testid={`checklist-next-${t.id}`}>
                  Nothing scheduled in the next six months
                </div>
              )}
            </div>
            {!readOnly && <button onClick={() => { setRead(null); setEdit(forEdit(t, config)); }} data-testid={`checklist-edit-${t.id}`}
              className="rounded-full border border-sand-300 px-4 py-2 text-sm font-semibold hover:border-sand-400">Edit</button>}
            {!readOnly && <button onClick={() => remove(t.id)} data-testid={`checklist-delete-${t.id}`}
              className="rounded-full p-2 text-sand-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {!templates.length && (
          <p className="rounded-2xl border border-dashed border-sand-300 p-8 text-center text-sm text-muted-foreground" data-testid="checklists-builder-empty">
            No checklists yet. Build one and the vendor gets it on their link automatically.
          </p>
        )}
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button onClick={() => { setRead(null); setEdit(blank(config, "")); }} data-testid="checklist-new"
              className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-redline-600">
              <Plus className="h-4 w-4" /> New checklist
            </button>
            {uploadBtn("checklist-import", "Read a spreadsheet")}
            <p className="w-full text-xs text-muted-foreground">
              Drop in the vendor's own .xlsx or .csv — it comes back laid out as a checklist for you to check over.
              Nothing is saved until you press Save.
            </p>
          </div>
        )}
      </div>

      {!readOnly && <div className="rounded-2xl border border-sand-200 bg-white p-6" data-testid="work-links">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-redline-500" />
          <h3 className="font-heading text-base font-bold tracking-tight">Vendor work links</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          One permanent link per person who is actually on a checklist here. They save it to their phone once —
          it always opens whatever is due that day. No login, no app.
        </p>
        {offices.length > 0 && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-4" data-testid="offices-missing">
            <p className="text-sm font-semibold text-amber-900">
              {offices.length === 1 ? "One company has" : `${offices.length} companies have`} nobody in the office on file
            </p>
            <p className="mt-1 text-sm text-amber-900/80">
              The office contact is who gets asked to pick someone when a list ends up with nobody — employees come
              and go, that number doesn’t. Add one and you never have to know their staff.
            </p>
            <div className="mt-3 space-y-2">
              {offices.map((o) => (
                <div key={o.vendor_id} className="flex flex-wrap items-center gap-2" data-testid={`office-missing-${o.vendor_id}`}>
                  <span className="min-w-[160px] flex-1 text-sm">
                    <b className="font-semibold">{o.vendor}</b>
                    <span className="text-muted-foreground"> · {o.lists.join(", ")}</span>
                  </span>
                  <button onClick={() => setOfficeFor({ vendor_id: o.vendor_id, vendor: o.vendor, name: "", phone: "", email: "" })}
                    data-testid={`office-add-${o.vendor_id}`}
                    className="rounded-full bg-ink px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-ink/90">
                    Add their office
                  </button>
                </div>
              ))}
            </div>
            {officeFor && (
              <div className="mt-3 rounded-2xl border border-sand-200 bg-white p-4" data-testid="office-form">
                <p className="text-sm font-semibold">Who answers the phone at {officeFor.vendor}?</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  <input className={input} placeholder="Their name" value={officeFor.name} data-testid="office-name"
                    onChange={(e) => setOfficeFor({ ...officeFor, name: e.target.value })} />
                  <input className={input} placeholder="Mobile" value={officeFor.phone} data-testid="office-phone"
                    onChange={(e) => setOfficeFor({ ...officeFor, phone: e.target.value })} />
                  <input className={input} placeholder="Or email" value={officeFor.email} data-testid="office-email"
                    onChange={(e) => setOfficeFor({ ...officeFor, email: e.target.value })} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={saveOffice} disabled={busy || !officeFor.name.trim() || !(officeFor.phone.trim() || officeFor.email.trim())}
                    data-testid="office-save"
                    className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 px-4 py-2 text-sm font-semibold text-white hover:bg-redline-600 disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Add them and send their link
                  </button>
                  <button onClick={() => setOfficeFor(null)} data-testid="office-cancel"
                    className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-4 divide-y divide-sand-100">
          {(links || []).map((l) => (
            <div key={l.contact_id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`work-link-${l.contact_id}`}>
              <div className="min-w-[160px] flex-1">
                <div className="text-sm font-semibold">
                  {l.name} <span className="font-normal text-muted-foreground">· {l.vendor}</span>
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${
                    l.office ? "bg-ink/90 text-white" : "bg-sand-100 text-ink/60"}`}
                    data-testid={`work-link-role-${l.contact_id}`}>
                    {l.office ? "Office" : "On site"}
                  </span>
                </div>
                {l.holds.length > 0 && (
                  <div className="text-xs text-muted-foreground" data-testid={`work-link-holds-${l.contact_id}`}>
                    Holds {l.holds.join(", ")}
                  </div>
                )}
                <div className="truncate text-xs text-muted-foreground">{l.link}</div>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(l.link); toast.success("Link copied"); }}
                data-testid={`work-link-copy-${l.contact_id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-3.5 py-1.5 text-xs font-semibold hover:border-sand-400">
                <Copy className="h-3 w-3" /> Copy
              </button>
              <button data-testid={`work-link-send-${l.contact_id}`}
                onClick={async () => {
                  try { await api.post(`/tenants/${tenantId}/checklists/links/${l.contact_id}/send`); toast.success(`Texted to ${l.name}`); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-3.5 py-1.5 text-xs font-semibold hover:border-sand-400">
                <Send className="h-3 w-3" /> Send it to them
              </button>
              <button data-testid={`work-link-off-${l.contact_id}`} onClick={() => markOff(l)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-3.5 py-1.5 text-xs font-semibold text-ink/60 hover:border-rose-300 hover:text-rose-600 disabled:opacity-50">
                <UserMinus className="h-3 w-3" /> No longer there
              </button>
            </div>
          ))}
          {links === null && <p className="py-3 text-sm text-muted-foreground">Checking who's on a checklist…</p>}
          {links?.length === 0 && <p className="py-3 text-sm text-muted-foreground">Nobody is on a checklist yet — a link appears here the moment you put someone on one.</p>}
        </div>
      </div>}
    </div>
  );

  const sections = [...new Set(edit.tasks.map((t) => t.section).filter(Boolean))];
  const preview = nextDates(edit, today || new Date().toISOString().slice(0, 10), 3);
  return (
    <div className="rounded-2xl border border-sand-200 bg-white p-6" data-testid="checklist-editor">
      {whoFor && (
        <WhoDialog tenantId={tenantId} row={whoFor} onClose={() => setWhoFor(null)}
          onSaved={async () => {
            onSaved?.(); loadLinks();
            // The pill sits inside the open editor, so refresh its copy of the row too.
            try {
              const { data } = await api.get(`/tenants/${tenantId}/checklists`);
              const fresh = (data.checklists || []).find((t) => t.id === edit.id);
              if (fresh) setEdit((e) => ({ ...e, holder: fresh.holder, last_on_site: fresh.last_on_site,
                                           waiting_on_office: fresh.waiting_on_office }));
            } catch { /* the list behind it is already reloading */ }
          }} />
      )}
      {askPanel()}
      {read && (
        <div className="mb-5 rounded-2xl border border-redline-200 bg-redline-50/60 p-5" data-testid="editor-import-note">
          <p className="text-sm font-semibold">
            Read {read.tasks.length} task{read.tasks.length === 1 ? "" : "s"} from {read.file}
            {read.sections.length > 1 ? ` across ${read.sections.length} areas` : ""}
            {read.read_by === "ai" ? " — the columns weren't clear, so the sheet was interpreted" : ""}.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Check it over below — the areas, the wording, and which tasks need a photo.
            Nothing is saved until you press Save, and you can change any of it first.
          </p>
          {(read.schedule?.dow || []).length > 0 && (
            <p className="mt-2 text-sm font-semibold" data-testid="editor-import-schedule">
              Days taken from the sheet: {read.schedule.dow.map((d) => (DAYS.find((x) => x[0] === d) || [])[1]).join(" · ")}
              <span className="font-normal text-muted-foreground"> — each task only appears on the days it was ticked for.</span>
            </p>
          )}
          {(read.monthly || []).length > 0 && (
            <p className="mt-1.5 text-sm font-semibold" data-testid="editor-import-monthly">
              {read.monthly.length} task{read.monthly.length === 1 ? " is" : "s are"} marked Monthly
              <span className="font-normal text-muted-foreground"> — a weekly checklist can't run them,
              so they're waiting as their own monthly checklist the moment you save this one.</span>
            </p>
          )}
          {read.ignored.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Left out of the checklist</p>
              <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground" data-testid="editor-import-ignored">
                {read.ignored.slice(0, 8).map((x, i) => <li key={i}>· {x}</li>)}
                {read.ignored.length > 8 && <li>· and {read.ignored.length - 8} more</li>}
              </ul>
            </div>
          )}
          <button onClick={() => setRead(null)} data-testid="editor-import-dismiss"
            className="mt-3 text-xs font-semibold text-ink-700 underline underline-offset-2 hover:text-ink">Got it</button>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" hint="what the vendor sees at the top of their phone">
          <input className={input} value={edit.name} data-testid="editor-name"
            onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Building cleaning" />
        </Field>
        <Field label="Who does it" hint={edit.vendor_id ? "" : "optional — it saves as a draft until someone's on it"}>
          {edit.id && edit.vendor_id && !readOnly && (
            <div className="mb-2 space-y-1.5">
              <WhoPill row={edit} onClick={() => setWhoFor(edit)} />
              {edit.last_on_site && edit.last_on_site !== edit.holder && (
                <button onClick={() => setWhoFor(edit)} data-testid="editor-on-site-nudge"
                  className="block text-left text-xs font-semibold text-amber-800 underline underline-offset-2">
                  {edit.last_on_site} has been the one turning up — put them on it →
                </button>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <select className={input} value={edit.vendor_id || ""} data-testid="editor-vendor"
              onChange={(e) => setEdit({ ...edit, vendor_id: e.target.value })}>
              <option value="">Not assigned yet</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.vendor_type === "individual" ? " · person" : ""}
                </option>
              ))}
            </select>
            <button onClick={() => { setInvite({ name: "", phone: "", email: "", company: "" }); }}
              data-testid="editor-vendor-add" title="Add someone who isn't on the platform yet"
              className="shrink-0 rounded-xl border border-sand-300 bg-white px-3 text-sm font-semibold hover:border-sand-400">
              <UserPlus className="h-4 w-4" />
            </button>
          </div>
          {!edit.vendor_id && (
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid="editor-vendor-draft-note">
              Nothing is scheduled and nobody is texted until someone's on it. Attach them whenever you're ready.
            </p>
          )}
          {edit.id && edit.vendor_id && (
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid="editor-vendor-hired-note">
              Who you hired. If the person doing the work has changed, leave this and change the name above —
              that's who gets the texts and the link.
            </p>
          )}
        </Field>
      </div>

      {invite && (
        <div className="mt-4 rounded-2xl border border-redline-200 bg-redline-50/60 p-5" data-testid="vendor-invite">
          <p className="font-heading text-[15px] font-bold tracking-tight">Who's doing this work?</p>
          <p className="mt-1 text-sm text-muted-foreground">
            A name and a mobile is all it takes — they get their own link, the list and the reminders,
            and nothing else to fill in. Add a company only if this is a firm you want on file with
            insurance and a signed agreement; then they'll get a setup link instead.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input className={input} placeholder="Who gets it" value={invite.name} data-testid="vendor-invite-name"
              onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            <input className={input} placeholder="Their mobile" value={invite.phone} data-testid="vendor-invite-phone"
              onChange={(e) => setInvite({ ...invite, phone: e.target.value })} />
            <input className={input} placeholder="Or their email" value={invite.email} data-testid="vendor-invite-email"
              onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            <input className={input} placeholder="Company (optional)" value={invite.company} data-testid="vendor-invite-company"
              onChange={(e) => setInvite({ ...invite, company: e.target.value })} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={addVendor} disabled={busy} data-testid="vendor-invite-send"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {(invite.company || "").trim() ? "Add them and send the setup link" : "Add them and send the list"}
            </button>
            <button onClick={() => setInvite(null)} data-testid="vendor-invite-cancel"
              className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-sand-200 bg-sand-50/60 p-4" data-testid="editor-schedule">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-redline-500" />
          <span className="text-sm font-semibold">How often</span>
        </div>

        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Field label="It runs">
            <select className={input} value={edit.freq || "weekly"} data-testid="editor-freq"
              onChange={(e) => {
                const freq = e.target.value;
                // An interval cadence counts from a date, so make that date visible rather than
                // letting the server pick one the preview can't know about.
                const needsAnchor = ["every_n_weeks", "every_n_days"].includes(freq);
                // Per-task weekday scoping is meaningless once a checklist runs on one date at a
                // time — left behind it quietly hides a task from the only visit there is.
                const tasks = NEEDS_DAYS.includes(freq)
                  ? edit.tasks : edit.tasks.map((t) => ({ ...t, days: "all" }));
                setEdit({ ...edit, freq, tasks,
                  start_date: needsAnchor && !edit.start_date ? (today || new Date().toISOString().slice(0, 10)) : edit.start_date });
              }}>
              {FREQS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>

          {edit.freq === "every_n_weeks" && (
            <Field label="Every how many weeks" hint="counted from the start date">
              <input type="number" min="1" max="12" className={input} value={edit.interval ?? 2} data-testid="editor-interval-weeks"
                onChange={(e) => setEdit({ ...edit, interval: e.target.value })} />
            </Field>
          )}
          {edit.freq === "every_n_days" && (
            <Field label="Every how many days" hint="counted from the start date">
              <input type="number" min="1" max="180" className={input} value={edit.interval ?? 1} data-testid="editor-interval-days"
                onChange={(e) => setEdit({ ...edit, interval: e.target.value })} />
            </Field>
          )}
          {edit.freq === "monthly_day" && (
            <Field label="On which date" hint="a 31st falls back to the last day of a short month">
              <input type="number" min="1" max="31" className={input} value={edit.month_day ?? 1} data-testid="editor-month-day"
                onChange={(e) => setEdit({ ...edit, month_day: e.target.value })} />
            </Field>
          )}
          {edit.freq === "monthly_nth" && (
            <Field label="On which weekday">
              <div className="flex gap-2">
                <select className={input} value={edit.nth ?? 1} data-testid="editor-nth"
                  onChange={(e) => setEdit({ ...edit, nth: Number(e.target.value) })}>
                  {NTHS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select className={input} value={edit.nth_dow || "mon"} data-testid="editor-nth-dow"
                  onChange={(e) => setEdit({ ...edit, nth_dow: e.target.value })}>
                  {DAYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </Field>
          )}
        </div>

        {NEEDS_DAYS.includes(edit.freq || "weekly") && (
          <div className="mt-4">
            <Field label="Which days">
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map(([k, l]) => (
                  <button key={k} data-testid={`editor-day-${k}`}
                    onClick={() => setEdit({ ...edit, dow: edit.dow.includes(k) ? edit.dow.filter((d) => d !== k) : [...edit.dow, k] })}
                    className={`h-9 w-12 rounded-lg text-xs font-bold transition-colors ${
                      edit.dow.includes(k) ? "bg-ink text-white" : "bg-white text-ink-700 hover:bg-sand-200"}`}>{l}</button>
                ))}
              </div>
            </Field>
          </div>
        )}

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Starts" hint="optional — also what “every 2 weeks” counts from">
            <input type="date" className={input} value={edit.start_date || ""} data-testid="editor-start-date"
              onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} />
          </Field>
          <Field label="Ends" hint="optional — leave empty to run indefinitely">
            <input type="date" className={input} value={edit.end_date || ""} data-testid="editor-end-date"
              onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} />
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Skip these dates" hint="holidays, closures — no visit is created at all">
            <div className="flex flex-wrap items-center gap-2">
              {(edit.skip_dates || []).map((d) => (
                <span key={d} data-testid={`editor-skip-${d}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-3 py-1 text-xs font-semibold">
                  {holName(d) ? `${holName(d)} · ${pretty(d, { month: "short", weekday: undefined })}`
                              : pretty(d, { weekday: "short", month: "short" })}
                  <button onClick={() => setEdit({ ...edit, skip_dates: edit.skip_dates.filter((x) => x !== d) })}
                    data-testid={`editor-skip-remove-${d}`} className="text-sand-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
                </span>
              ))}
              <input type="date" value={skip} data-testid="editor-skip-input" onChange={(e) => setSkip(e.target.value)}
                className="rounded-lg border border-sand-200 bg-white px-2.5 py-1.5 text-xs outline-none" />
              <button data-testid="editor-skip-add" disabled={!skip}
                onClick={() => { setEdit({ ...edit, skip_dates: [...new Set([...(edit.skip_dates || []), skip])].sort() }); setSkip(""); }}
                className="rounded-full border border-sand-300 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Skip it</button>
            </div>
            {hols.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button onClick={addHolidays} data-testid="editor-holidays-add"
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-ink/90">
                  <CalendarDays className="h-3.5 w-3.5" /> Skip the usual holidays
                </button>
                <button onClick={() => setShowHols((v) => !v)} data-testid="editor-holidays-toggle"
                  className="text-xs font-semibold text-redline-600 hover:underline">
                  {showHols ? "Hide the list" : "Pick them one by one"}
                </button>
                <span className="text-xs text-muted-foreground">
                  New Year's, Memorial Day, the 4th, Labor Day, Thanksgiving + the Friday, Christmas Eve &amp; Day
                </span>
              </div>
            )}
            {showHols && (
              <div className="mt-2 rounded-xl border border-sand-200 bg-sand-50/60 p-3 sm:columns-2"
                data-testid="editor-holidays-list">
                {hols.map((h) => {
                  const on = (edit.skip_dates || []).includes(h.date);
                  const lands = landsOn(h.date);
                  return (
                    <label key={h.date} data-testid={`editor-holiday-${h.date}`}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-white">
                      <input type="checkbox" checked={on} className="accent-redline-500"
                        data-testid={`editor-holiday-toggle-${h.date}`}
                        onChange={() => setEdit({ ...edit, skip_dates: on
                          ? edit.skip_dates.filter((x) => x !== h.date)
                          : [...new Set([...(edit.skip_dates || []), h.date])].sort() })} />
                      <span className="font-semibold">{h.name}</span>
                      <span className="text-muted-foreground">{pretty(h.date, { weekday: "short", month: "short" })}</span>
                      {!lands && <span className="text-muted-foreground/70">· no visit that day</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </Field>
        </div>

        <p className="mt-4 border-t border-sand-200 pt-3 text-sm" data-testid="editor-schedule-preview">
          <span className="font-semibold">{cadence(edit)}</span>
          {preview.length
            ? <span className="text-muted-foreground"> · next: {preview.map((d) => pretty(d, { weekday: "short", month: "short" })).join(" · ")}</span>
            : <span className="text-amber-700"> · nothing scheduled in the next six months</span>}
          {edit.end_date && <span className="text-muted-foreground"> · stops after {pretty(edit.end_date, { weekday: "short", month: "short" })}</span>}
          {(edit.skip_dates || []).length > 0 && <span className="text-muted-foreground"> · {edit.skip_dates.length} skipped</span>}
        </p>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Field label="Service window starts" hint="optional">
          <input type="time" className={input} value={edit.window_start || ""} data-testid="editor-window-start"
            onChange={(e) => setEdit({ ...edit, window_start: e.target.value })} />
        </Field>
        <Field label="Done by">
          <input type="time" className={input} value={edit.due_by || ""} data-testid="editor-due-by"
            onChange={(e) => setEdit({ ...edit, due_by: e.target.value })} />
        </Field>
        <Field label="Reminder text">
          <select className={input} value={edit.remind} data-testid="editor-remind"
            onChange={(e) => setEdit({ ...edit, remind: e.target.value })}>
            <option value="window_start">When the window opens</option>
            <option value="hours_before">A few hours before the deadline</option>
            <option value="off">Don't remind them</option>
          </select>
        </Field>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Who gets the texts">
          <select className={input} value={edit.notify_to} data-testid="editor-notify"
            onChange={(e) => setEdit({ ...edit, notify_to: e.target.value })}>
            <option value="both">The office and the person on site</option>
            <option value="office">The vendor's office only</option>
            <option value="onsite">The person on site only</option>
          </select>
        </Field>
        <Field label="When everything is ticked and photographed">
          <select className={input} value={edit.auto_approve ? "auto" : "manual"} data-testid="editor-auto-approve"
            onChange={(e) => setEdit({ ...edit, auto_approve: e.target.value === "auto" })}>
            <option value="manual">Wait for me to approve it</option>
            <option value="auto">Approve it automatically</option>
          </select>
        </Field>
        <Field label="Nudge the vendor" hint="minutes before the deadline, if nothing is ticked">
          <input type="number" min="0" max="480" placeholder={`Community default · ${config?.nudge_minutes ?? 60} min`}
            className={input} value={edit.nudge_minutes} data-testid="editor-nudge"
            onChange={(e) => setEdit({ ...edit, nudge_minutes: e.target.value })} />
        </Field>
        <Field label="If the window closes with nothing ticked">
          <select className={input} value={edit.mark_missed} data-testid="editor-mark-missed"
            onChange={(e) => setEdit({ ...edit, mark_missed: e.target.value })}>
            <option value="">Community default — {config?.mark_missed === false ? "leave it open" : "record it as missed"}</option>
            <option value="yes">Record it as missed</option>
            <option value="no">Leave it open</option>
          </select>
        </Field>
        <Field label="Can the crew add items?" hint="they choose “just today” or “every time”; you can undo it from the record">
          <select className={input} data-testid="editor-crew-can-add"
            value={edit.crew_can_add === null || edit.crew_can_add === undefined ? "" : (edit.crew_can_add ? "yes" : "no")}
            onChange={(e) => setEdit({ ...edit, crew_can_add: e.target.value === "" ? null : e.target.value === "yes" })}>
            <option value="">Community default — {config?.crew_can_add ? "they can add items" : "nobody but the office"}</option>
            <option value="yes">Yes — they can add what they find</option>
            <option value="no">No — this list is the office's</option>
          </select>
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Tasks · {edit.tasks.length}
          <span className="ml-2 font-semibold normal-case tracking-normal text-muted-foreground/80">
            — {NEEDS_DAYS.includes(edit.freq || "weekly") && (edit.dow || []).length
              ? "tap days to put a task on some visits only · tap Photo to demand proof"
              : `${cadence(edit).toLowerCase()} — every task runs on that visit · tap Photo to demand proof`}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <label data-testid="editor-import"
            className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-redline-600 hover:underline">
            {reading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload2 className="h-3.5 w-3.5" />}
            {reading ? "Reading the sheet…" : "Read a spreadsheet"}
            <input type="file" accept=".xlsx,.xlsm,.csv,.tsv" className="hidden" onChange={pickFile}
              disabled={reading} data-testid="editor-import-input" />
          </label>
          <button onClick={() => setShowPaste((v) => !v)} data-testid="editor-paste-toggle"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-redline-600 hover:underline">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Paste rows
          </button>
        </div>
      </div>

      {showPaste && (
        <div className="mt-2 rounded-xl border border-sand-200 bg-sand-50 p-4">
          <p className="text-xs text-muted-foreground">One task per line: task, area, and the word “photo” if you want proof.</p>
          <textarea rows={5} value={paste} onChange={(e) => setPaste(e.target.value)} data-testid="editor-paste"
            placeholder={"Mop lobby floor, Lobby & entry, photo\nWipe entry glass, Lobby & entry"}
            className="mt-2 w-full resize-none rounded-lg border border-sand-200 px-3 py-2 font-mono text-xs outline-none" />
          <button onClick={doPaste} data-testid="editor-paste-read"
            className="mt-2 rounded-full bg-ink px-4 py-1.5 text-xs font-semibold text-white">Read it</button>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {edit.tasks.map((task, i) => (
          /* On a phone this is three stacked lines; `sm:contents` hands the children straight back
             to the row so the desktop layout is untouched. */
          <div key={i} data-testid={`editor-task-${i}`}
            className="rounded-xl border border-sand-200 bg-sand-50 p-2.5 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
            <div className="mb-2 flex items-center justify-between sm:mb-0 sm:contents">
              <span className="w-5 shrink-0 text-center font-mono text-xs text-sand-400">{i + 1}</span>
              <div className="flex items-center gap-1 sm:hidden">
                <button onClick={() => move(i, -1)} data-testid={`editor-task-up-m-${i}`} aria-label="Move up"
                  className="grid h-8 w-8 place-items-center rounded-lg text-sand-500 active:bg-sand-200"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} data-testid={`editor-task-down-m-${i}`} aria-label="Move down"
                  className="grid h-8 w-8 place-items-center rounded-lg text-sand-500 active:bg-sand-200"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => setEdit({ ...edit, tasks: edit.tasks.filter((_, j) => j !== i) })} aria-label="Remove task"
                  data-testid={`editor-task-remove-m-${i}`}
                  className="grid h-8 w-8 place-items-center rounded-lg text-sand-400 active:bg-sand-200 active:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <input className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none sm:min-w-[180px] sm:flex-1"
              value={task.label} data-testid={`editor-task-label-${i}`}
              onChange={(e) => setTask(i, { label: e.target.value })} placeholder="What has to be done" />
            <div className="mt-2 flex items-center gap-2 sm:mt-0 sm:contents">
              <input className="min-w-0 flex-1 rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none sm:w-[150px] sm:flex-none"
                value={task.section || ""} list="checklist-sections" data-testid={`editor-task-section-${i}`}
                onChange={(e) => setTask(i, { section: e.target.value })} placeholder="Area" />
              <button onClick={() => setTask(i, { photo_required: !task.photo_required })} data-testid={`editor-task-photo-${i}`}
                title={task.photo_required
                  ? "A photo is required before this can be ticked off"
                  : "No photo needed — tap to require one"}
                className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors ${
                  task.photo_required ? "border-amber-300 bg-amber-50 text-amber-800"
                                      : "border-sand-200 bg-white text-ink-700 hover:border-sand-400"}`}>
                <Camera className="h-4 w-4" /> {task.photo_required ? "Photo required" : "Photo"}
              </button>
            </div>
            <div className="mt-2 sm:mt-0 sm:contents">{dayPick(task, i)}</div>
            <div className="hidden sm:contents">
              <button onClick={() => move(i, -1)} data-testid={`editor-task-up-${i}`} className="grid h-9 w-9 place-items-center rounded-lg border border-sand-200 bg-white text-sand-500"><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} data-testid={`editor-task-down-${i}`} className="grid h-9 w-9 place-items-center rounded-lg border border-sand-200 bg-white text-sand-500"><ArrowDown className="h-4 w-4" /></button>
              <button onClick={() => setEdit({ ...edit, tasks: edit.tasks.filter((_, j) => j !== i) })} data-testid={`editor-task-remove-${i}`}
                className="grid h-9 w-9 place-items-center rounded-lg border border-sand-200 bg-white text-sand-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
            <Steps task={task} i={i} onChange={(steps) => setTask(i, { steps })} />
          </div>
        ))}
        <datalist id="checklist-sections">{sections.map((s) => <option key={s} value={s} />)}</datalist>
        <button onClick={() => setEdit({ ...edit, tasks: [...edit.tasks, { label: "", section: sections[0] || "Tasks", photo_required: !!config?.photo_required_default, days: "all" }] })}
          data-testid="editor-add-task"
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-sand-300 px-4 py-2 text-sm font-semibold text-muted-foreground hover:border-sand-400 hover:text-ink">
          <Plus className="h-4 w-4" /> Add a task
        </button>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button onClick={save} disabled={busy} data-testid="editor-save"
          className="inline-flex items-center gap-2 rounded-full bg-redline-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-redline-600 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save checklist
        </button>
        <button onClick={() => { setRead(null); setEdit(null); }} data-testid="editor-cancel" className="rounded-full border border-sand-300 px-5 py-2.5 text-sm font-semibold">Cancel</button>
      </div>
    </div>
  );
}
